/**
 * #1485 — chat state that has to survive a page reload or a closed chat.
 *
 * After a reload b2b-api hands the agent's answer to the session's new page as a proactive
 * message, as soon as the page registers: also when the widget restored collapsed, or has no
 * sessionId yet. Such a page's message state does not hold the conversation (`openChat` loads it
 * from localStorage later), so:
 *
 * - a finished answer is added to the stored conversation, not saved over it
 *   (`appendToStoredMessages`);
 * - opening the chat keeps an answer still arriving (`keepInFlight`) instead of replacing it with
 *   the stored list.
 *
 * The reload itself no longer waits for the answer, so messages the customer typed meanwhile —
 * queued behind the answer, never sent — travel in the restore data and are sent by the restored
 * page (`restoredQueueEntries`, `queueEntry`).
 */
import { loadMessages, saveMessages } from '../../services/messageHistory';

/**
 * The stored conversation, plus the live message `inFlightId` when storage does not have it yet.
 * Nothing else from the live state is kept: storage holds only the newest messages, and a page
 * rebuilt from the checkpoint carries other ids, so a wider merge reorders and duplicates.
 * @param {Array<{id: string}>} stored - messages loaded from localStorage
 * @param {Array<{id: string}>} live - the widget's current message state
 * @param {string|null} inFlightId - the proactive answer still arriving, if any
 * @returns {Array} `stored` itself when there is nothing to keep
 */
export function keepInFlight(stored, live, inFlightId) {
  if (!inFlightId || stored.some((message) => message.id === inFlightId)) return stored;
  const inFlight = (live || []).find((message) => message.id === inFlightId);
  return inFlight ? [...stored, inFlight] : stored;
}

/**
 * The id of a proactive bubble. A handed-over answer names the request it answers, and every
 * page that takes it over — two tabs of one session that both reloaded on the agent's
 * navigation — shows and stores it under that one id, so the shared conversation keeps a single
 * copy (`appendToStoredMessages`). Other proactive answers are named after their run.
 * @param {string} runId
 * @param {string|null|undefined} requestMessageId
 * @returns {string}
 */
export function proactiveMessageId(runId, requestMessageId) {
  return requestMessageId ? `proactive-${requestMessageId}` : `proactive-${runId}`;
}

/**
 * Append one finished message to the conversation stored for `sessionId`, once.
 * @param {string} sessionId
 * @param {{id: string, text?: string}|undefined} message
 * @returns {boolean} true when it was stored now
 */
export function appendToStoredMessages(sessionId, message) {
  if (!sessionId || !message || !String(message.text ?? '').trim()) return false;
  const stored = loadMessages(sessionId);
  if (stored.some((existing) => existing.id === message.id)) return false;
  return saveMessages(sessionId, [...stored, { ...message, isStreaming: false }]);
}

/**
 * A pending-queue entry: a message typed on this page (a string), or one a reload carried over,
 * whose bubble the restored page has to show again (its history comes from the checkpoint,
 * which never saw the message).
 * @param {string|{text: string, restored: boolean}} entry
 * @returns {{text: string, restored: boolean}}
 */
export function queueEntry(entry) {
  if (typeof entry === 'string') return { text: entry, restored: false };
  return { text: String(entry?.text ?? ''), restored: Boolean(entry?.restored) };
}

/**
 * The unsent messages a reload carried over, as queue entries.
 * @param {{pendingMessages?: unknown}|null|undefined} restoreData - the `sg_restore_widget` data
 * @returns {Array<{text: string, restored: true}>}
 */
export function restoredQueueEntries(restoreData) {
  const pending = restoreData?.pendingMessages;
  if (!Array.isArray(pending)) return [];
  return pending
    .filter((text) => typeof text === 'string' && text.trim())
    .map((text) => ({ text, restored: true }));
}
